import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {IVideocallConfig} from "../../../../models/videocall/i-videocall-confi";
import {Utils} from "../../../../utils";
import {DEBUG, DebugService} from "../../../../services/debug.service";
import {ScriptService} from "../../../../services/scripts/script-service";
import {VideocallMessageType} from "../../../../models/videocall/videocall-message-type";
import {IVideocall} from "../../../../models/videocall/i-videocall";
import {environment} from "@environments/environment";

declare var createUnityInstance: any;
declare var ExternalOnUnityMessage: any;

@Component({
  selector: 'app-videocall-unity',
  templateUrl: './videocall-unity.component.html',
  styleUrls: ['./videocall-unity.component.scss']
})
export class VideocallUnityComponent implements OnInit, IVideocall {

  @Output()
  onMessage = new EventEmitter<{ eventType: VideocallMessageType, data?: string }>();

  unityInstance: any;
  moduleLoaded: boolean;
  externalCommunicator = 'ExternalCommunicator';
  callManagerExpert = 'CallManagerExpert';

  constructor(
    private scriptService: ScriptService,
    private debugService: DebugService
  ) { }

  ngOnInit(): void {
  }

  async prepareVideocall(): Promise<void> {
    this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, 'Preparing videocall module...');

    try {

      // Load required script for module
      await this.scriptService.load('UnityLoader', 'UnityHooks');

      // Bind communicator
      ExternalOnUnityMessage = (msg: string) => this.onModuleMessage(msg);

      this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, 'Preparation completed');

    } catch (ex) {
      this.debugService.error(DEBUG.VIDEOCALL_UNITY_MODULE, ex);
    }
  }

  async startVideocall(): Promise<void> {

    this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, 'Checking container...');
    while (document.getElementById('unityContainer') == null) {
      await Utils.sleep(100);
    }

    this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, 'Starting videocall module...');

    // Set module config
    const legacyUrl = 'assets/videocall/legacy';
    const buildUrl = `${legacyUrl}/Build`;
    const config = {
      dataUrl: `${buildUrl}/Build.data`,
      frameworkUrl: `${buildUrl}/Build.framework.js`,
      codeUrl: `${buildUrl}/Build.wasm`,
      streamingAssetsUrl: `${legacyUrl}/StreamingAssets`,
      companyName: environment.companyName,
      productName: environment.videocall.productName,
      productVersion: environment.videocall.productVersion,
    };

    this.unityInstance = await createUnityInstance(
      document.querySelector("#unityContainer"),
      config,
      (progress) => {
        this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, `Loading... ${progress}`);
      }
    );

    this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, 'Videocall module started');

  }

  /**
   * Messages FROM module
   **/

  async onModuleMessage(msg: string): Promise<void> {

    // Ignore internal counter message type
    if (msg.split(';')[0] === 'internalcounter') return;

    this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, `New message from module: ${msg}`);

    // Take only first parts of msg: 'part0;part1'
    const msgParts = msg.split(';');
    msg = `${msgParts[0]};${msgParts[1]}`;

    switch (msg) {
      case VideocallMessageType.MODULE_LOADED:
        this.onMessage.emit({ eventType: VideocallMessageType.MODULE_LOADED });
        this.moduleLoaded = true;
        break;
      case VideocallMessageType.MODULE_SCENE_LOADED:
        this.onMessage.emit({ eventType: VideocallMessageType.MODULE_SCENE_LOADED });
        break;
      case VideocallMessageType.CALL_STARTED:
        this.onMessage.emit({ eventType: VideocallMessageType.CALL_STARTED });
        break;
      case VideocallMessageType.CALL_NEW_CONNECTION:
        this.onMessage.emit({ eventType: VideocallMessageType.CALL_NEW_CONNECTION });
        break;
      case VideocallMessageType.CALL_DISCONNECTED:
        this.onMessage.emit({ eventType: VideocallMessageType.CALL_DISCONNECTED });
        break;
      case VideocallMessageType.CALL_RECORD_STARTED:
        this.onMessage.emit({ eventType: VideocallMessageType.CALL_RECORD_STARTED });
        break;
      case VideocallMessageType.CALL_RECORD_STOPPED:
        this.onMessage.emit({ eventType: VideocallMessageType.CALL_RECORD_STOPPED });
        break;
      case VideocallMessageType.ANNOTATION_TEXT:
        this.onMessage.emit({ eventType: VideocallMessageType.ANNOTATION_TEXT });
        break;
      case VideocallMessageType.CALL_CLOSED:
        // TODO: possible bug with mobile: it is sending 'close' and not 'dm;close'
        this.onMessage.emit({ eventType: VideocallMessageType.CALL_CLOSED });
        break;
      case VideocallMessageType.MODULE_UNLOADED:
        this.onMessage.emit({ eventType: VideocallMessageType.MODULE_UNLOADED });
        break;
      default:
        this.debugService.error(DEBUG.VIDEOCALL_UNITY_MODULE, `Message from module not handled: ${msg}`);
    }

  }

  /**
   * Messages TO module
   **/

  goToExpert(): void {
    this.sendToModule(this.externalCommunicator, 'GoToExpert', '');
  }

  startScotty(config: IVideocallConfig): void {
    this.sendToModule(this.externalCommunicator, 'StartScotty', JSON.stringify(config));
  }

  closeCall(): void {
    this.moduleLoaded = false;
    this.sendToModule(this.externalCommunicator, 'CloseCall', '');
  }

  unityQuit(): void {
    this.sendToModule(this.callManagerExpert, 'UnityQuit', '');
  }

  freeze(): void {
    this.sendToModule(this.externalCommunicator, 'Freeze', '');
  }

  unfreeze(): void {
    this.sendToModule(this.externalCommunicator, 'Unfreeze', '');
  }

  enrichText(data: string): void {
    this.sendToModule(this.externalCommunicator, 'EnrichText', data);
  }

  cancelText(): void {
    this.sendToModule(this.externalCommunicator, 'CancelText', '');
  }

  selectArrow(data: string): void {
    this.sendToModule(this.externalCommunicator, 'SelectArrow', data);
  }

  selectShape(data: string): void {
    this.sendToModule(this.externalCommunicator, 'SelectShape', data);
  }

  showMeasurements(): void {
    this.sendToModule(this.externalCommunicator, 'ShowMeasurements', '');
  }

  hideMeasurements(): void {
    this.sendToModule(this.externalCommunicator, 'HideMeasurements', '');
  }

  selectFreedraw(data: string): void {
    this.sendToModule(this.externalCommunicator, 'SelectFreedraw', data);
  }

  selectText(): void {
    this.sendToModule(this.externalCommunicator, 'SelectText', '');
  }

  clear(): void {
    this.sendToModule(this.externalCommunicator, 'Clear', '');
  }

  setMute(muted: boolean): void {
    this.sendToModule(this.externalCommunicator, 'SetMute', muted.toString());
  }

  addImage(url: string): void {
    this.sendToModule(this.externalCommunicator, 'AddImage', url);
  }

  addModel(url: string): void {
    this.sendToModule(this.externalCommunicator, 'AddModel', url);
  }

  addPdf(url: string): void {
    this.sendToModule(this.externalCommunicator, 'AddPdf', url);
  }

  takeScreenshot() {
    throw new Error('Method not implemented on Unity module. Use Babylon module instead.');
  }

  directMessage(message: string): void {
    this.sendToModule(this.externalCommunicator, 'DirectMessage', message);
  }

  notifyRecording(data: string): void {
    this.sendToModule(this.externalCommunicator, 'NotifyRecording', data);
  }

  forceResize(): void {
    /* Nothing to do */
  }

  sendToModule(communicator: string, method: string, data: string): void {
    const _data = [communicator, method, data];
    this.debugService.log(DEBUG.VIDEOCALL_UNITY_MODULE, `Sending message to module: ${_data.filter(x => !!x).join(', ')}`);
    this.unityInstance.SendMessage(..._data);
  }

}
